// ── Friet Fighter — impact-FX (hit-sparks) ──
// Lichtgewicht particle-burst op het rake-punt. Render-side (Math.random mag,
// raakt de deterministische sim niet). Additieve quads → heldere vonken.

import * as THREE from 'three';

const POOL = 28;

interface Spark {
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  vx: number;
  vy: number;
  life: number;
  max: number;
}

export class ImpactFX {
  readonly group = new THREE.Group();
  private sparks: Spark[] = [];
  private flash: THREE.Mesh;
  private flashMat: THREE.MeshBasicMaterial;
  private flashLife = 0;
  private flashMax = 0.16;

  constructor(base = '') {
    // Echte hit-spark-sprite (nano-banana, transparant); wit/geel → tint zuiver per kleur.
    new THREE.TextureLoader().load(
      `${base}assets/fx/hitspark.png`,
      (t) => {
        t.colorSpace = THREE.SRGBColorSpace;
        this.flashMat.map = t;
        this.flashMat.needsUpdate = true;
      },
      undefined,
      () => {
        // Optioneel → vlakke witte flits-fallback. DEV-only melding (prod strip't dit).
        if (import.meta.env.DEV) console.warn('[ff] hit-spark sprite mist: assets/fx/hitspark.png — flits-fallback');
      },
    );

    const geo = new THREE.PlaneGeometry(0.22, 0.22);
    for (let i = 0; i < POOL; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffdd55,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.visible = false;
      this.group.add(mesh);
      this.sparks.push({ mesh, mat, vx: 0, vy: 0, life: 0, max: 1 });
    }
    this.flashMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.flash = new THREE.Mesh(new THREE.PlaneGeometry(1.0, 1.0), this.flashMat);
    this.flash.visible = false;
    this.group.add(this.flash);
  }

  /** Spawn een impact op (x,y). scale ~1 normaal, ~2 KO. */
  spawn(x: number, y: number, scale = 1, color = 0xffdd55): void {
    this.flash.position.set(x, y, 0.6);
    this.flash.scale.setScalar(scale * 1.6); // spark-burst iets groter dan de oude flits
    this.flash.rotation.z = Math.random() * Math.PI * 2; // willekeurige hoek per hit
    this.flashMat.color.setHex(color); // tint de witte spark naar de special-kleur
    this.flashMat.opacity = 1;
    this.flash.visible = true;
    this.flashLife = this.flashMax;

    const count = Math.min(POOL, Math.round(16 * scale));
    let spawned = 0;
    for (const s of this.sparks) {
      if (spawned >= count) break;
      if (s.life > 0) continue;
      const a = Math.random() * Math.PI * 2;
      const sp = (1.8 + Math.random() * 3) * scale;
      s.vx = Math.cos(a) * sp;
      s.vy = Math.sin(a) * sp + 1.2;
      s.max = s.life = 0.2 + Math.random() * 0.16;
      s.mesh.position.set(x, y, 0.6);
      s.mesh.scale.setScalar(scale * (0.6 + Math.random() * 0.7));
      s.mat.color.set(color);
      s.mat.opacity = 1;
      s.mesh.visible = true;
      spawned++;
    }
  }

  update(dt: number): void {
    if (this.flashLife > 0) {
      this.flashLife -= dt;
      const t = Math.max(0, this.flashLife / this.flashMax);
      this.flashMat.opacity = 0.95 * t;
      this.flash.scale.multiplyScalar(1 + dt * 4);
      if (this.flashLife <= 0) this.flash.visible = false;
    }
    for (const s of this.sparks) {
      if (s.life <= 0) continue;
      s.life -= dt;
      const t = Math.max(0, s.life / s.max);
      s.mesh.position.x += s.vx * dt;
      s.mesh.position.y += s.vy * dt;
      s.vy -= 9 * dt; // zwaartekracht
      s.mat.opacity = t;
      s.mesh.scale.multiplyScalar(Math.max(0, 1 - dt * 2.5));
      if (s.life <= 0) s.mesh.visible = false;
    }
  }
}
